import { useRouter } from "next/router";
import { useEffect } from "react";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import Link from "next/link";

import couponCore from "../../core/coupon.core";
import { useTranslation } from "../../core/translation.core";
import { Coupon, CouponFormData } from "../../types/coupon";
import { LocalInputDefaultLocale } from "../locale/locale.comp";
import { EditorSection, SubmitPanel, Title } from "../reuseables/editor.comp";
import { EditingMode, FancyCheckbox, LabelAndInput, LabelAndSelect } from "../reuseables/form.comp";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import MultipleMembershipsSelector from "../reuseables/multipleMembershipsSelector";
import MultipleCategoriesSelector from "../reuseables/multipleCategoriesSelector";
import MultipleProductsSelector from "../reuseables/multipleProductsSelector";
import ProductRestrictionForm from "../shipSetting/productRestriction.comp";
import CategoryRestrictionForm from "../shipSetting/categoryRestriction.comp";
import PlacementRestrictionForm from "../shipSetting/placementRestriction.comp";

interface PropsCreate {
  editingMode: EditingMode.create;
}

interface PropsEdit {
  editingMode: EditingMode.edit;
  couponId: string;
}

export default function CouponEditor(props: PropsCreate | PropsEdit) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const methods = useForm<CouponFormData>({
    defaultValues: {
      discountType: "percent",
      expirationType: "date",
      issueMode: "manual",
      eligibility: { placement: "both" },
    },
  });
  const router = useRouter();

  const [issueMode] = useWatch({ control: methods.control, name: ["issueMode"] });

  function getExpirationType(coupon: Coupon) {
    if (!coupon) return "day";

    if (coupon.expiration.date) {
      return "date";
    } else {
      return "day";
    }
  }

  const { data: coupon } = useQuery({
    queryKey: ["coupon", (props as PropsEdit).couponId],
    queryFn: (ctx) => couponCore.getOne(ctx.queryKey[1]),
    enabled: props.editingMode === EditingMode.edit && props.couponId !== undefined,
  });

  useEffect(() => {
    if (coupon) {
      coupon.expirationType = getExpirationType(coupon);

      coupon.discountType = coupon.discount.amt ? "fixed" : "percent";
      methods.reset(coupon);
    }
  }, [coupon, methods]);

  const onSubmit = async (data: CouponFormData) => {
    sanitizeCouponFormData(data);

    switch (props.editingMode) {
      case EditingMode.create:
        await couponCore.create(data);
        await queryClient.invalidateQueries({ queryKey: ["coupons"] });
        router.push("/coupons");
        break;
      case EditingMode.edit:
        await couponCore.edit(data, props.couponId);

        await queryClient.invalidateQueries({ queryKey: ["coupons"] });
        await queryClient.invalidateQueries({ queryKey: ["coupon"] });
        router.push("/coupons");
        break;
      default:
        break;
    }
  };

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="pb-20">
      <EditorSection>
        <Title>{t(msg.product.generalInfo)}</Title>
        <div className="flex flex-col gap-4">
          <LocalInputDefaultLocale register={methods.register} fieldName="name" label={t(msg.product.name)} />
          <LabelAndInput register={methods.register} fieldName="code" label={t(msg.category.code)} required={true} />
        </div>
      </EditorSection>
      <EditorSection>
        <Title>{t(msg.membership.issueCriteria)}</Title>
        <div>
          <LabelAndSelect
            methods={methods}
            fieldName="issueMode"
            topLabelCss="block"
            label={t(msg.membership.activationMethod)}
            setDefault={false}
            options={[
              { label: t(msg.product.auto), value: "auto" },
              { label: t(msg.product.manual), value: "manual" },
            ]}
          />
          {issueMode === "auto" ? <AutoIssueCriteria methods={methods} /> : null}
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.coupon.eligibility)}</Title>
        <div className="flex flex-col gap-5">
          <LabelAndSelect
            methods={methods}
            fieldName="eligibility.placement"
            registerOptions={{ required: true }}
            label={t(msg.coupon.placement)}
            topLabelCss="block"
            options={[
              { label: t(msg.placement.eshop), value: "online" },
              { label: t(msg.placement.retail), value: "pos" },
              { label: t(msg.placement.both), value: "both" },
            ]}
          />

          <FancyCheckbox value={true} register={methods.register("eligibility.withOtherCoupon")}>
            {t(msg.coupon.combineWithOtherCoupon)}
          </FancyCheckbox>

          <LabelAndInput
            register={methods.register}
            required={true}
            type="number"
            fieldName="eligibility.priority"
            label={t(msg.coupon.priority)}
            topLabelCss="block"
          />

          <FancyCheckbox value={true} register={methods.register("eligibility.autoApply")}>
            {t(msg.coupon.autoApply)}
          </FancyCheckbox>

          <LabelAndInput
            register={methods.register}
            fieldName="eligibility.orderOverAmt"
            label={t(msg.coupon.minOrderAmount)}
            topLabelCss="block"
            type="number"
          />
          <LabelAndInput
            register={methods.register}
            fieldName="eligibility.userUsageLimit"
            label={t(msg.coupon.userUsageLimit)}
            topLabelCss="block"
            type="number"
          />
          <LabelAndInput
            register={methods.register}
            fieldName="eligibility.userUsageLimitDuration"
            label={t(msg.coupon.userUsageLimitDuration)}
            topLabelCss="block"
            type="number"
          />
          <LabelAndInput
            register={methods.register}
            fieldName="eligibility.shopUsageLimit"
            label={t(msg.coupon.shopUsageLimit)}
            topLabelCss="block"
            type="number"
          />
          <LabelAndInput
            register={methods.register}
            fieldName="eligibility.shopUsageLimitDuration"
            label={t(msg.coupon.shopUsageLimitDuration)}
            topLabelCss="block"
            type="number"
          />
          <LabelAndInput
            register={methods.register}
            fieldName="eligibility.firstTimeRegAfter"
            type="date"
            label={t(msg.coupon.accountCreatedAfter)}
            topLabelCss="block"
          />
          <div className="flex flex-col gap-2">
            <span className="block text-xs font-semibold">{t(msg.coupon.includeMemberships)}</span>
            <MultipleMembershipsSelector
              methods={methods}
              fieldName="eligibility.includeMembershipIds"
              buttonText={t(msg.coupon.addIncludeMemberships)}
              btnId="add-include-memberships-btn"
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="block text-xs font-semibold">{t(msg.coupon.excludeMemberships)}</span>
            <MultipleMembershipsSelector
              methods={methods}
              fieldName="eligibility.excludeMembershipIds"
              buttonText={t(msg.coupon.addExcludeMemberships)}
              btnId="add-exclude-memberships-btn"
            />
          </div>
          <CategoryRestrictionForm<CouponFormData>
            methods={methods}
            typeFieldName="eligibility.categoryRestriction.type"
            idsFieldName="eligibility.categoryRestriction.restrictToIds"
          />
          <ProductRestrictionForm<CouponFormData>
            methods={methods}
            typeFieldName="eligibility.productRestriction.type"
            idsFieldName="eligibility.productRestriction.restrictToIds"
          />
          <PlacementRestrictionForm<CouponFormData>
            methods={methods}
            typeFieldName="eligibility.placementRestriction.type"
            eshopFieldName="eligibility.placementRestriction.restrictToEshop"
            retailIdsFieldName="eligibility.placementRestriction.restrictToRetailIds"
          />
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.discount.discount)}</Title>

        <div className="flex flex-col gap-4">
          <LabelAndSelect
            methods={methods}
            fieldName="discountType"
            label={t(msg.discount.discountType)}
            topLabelCss="block"
            setDefault={false}
            options={[
              { label: t(msg.discount.percentage), value: "percent" },
              { label: t(msg.discount.fixedAmt), value: "fixed" },
            ]}
          />
          <DiscountInput methods={methods} />
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.coupon.expiration)}</Title>

        <div className="flex flex-col gap-4">
          <LabelAndInput
            register={methods.register}
            fieldName="startDate"
            type="date"
            label={t(msg.coupon.startDate)}
            topLabelCss="block"
          />
          <ExpirationType methods={methods} />
          <ExpirationDateOrDay methods={methods} />
        </div>
      </EditorSection>

      <SubmitButton editingMode={props.editingMode} />
    </form>
  );
}

function DiscountInput({ methods }: { methods: UseFormReturn<CouponFormData, any, undefined> }) {
  const { t, msg } = useTranslation();
  const discountType = useWatch({ control: methods.control, name: "discountType" });

  switch (discountType) {
    case "percent":
      return (
        <LabelAndInput
          register={methods.register}
          fieldName="discount.per"
          label={t(msg.coupon.discountPercentage)}
          topLabelCss="block"
          type="number"
          required={true}
        />
      );
    case "fixed":
      return (
        <LabelAndInput
          register={methods.register}
          fieldName="discount.amt"
          label={t(msg.coupon.discountAmount)}
          topLabelCss="block"
          type="number"
          required={true}
        />
      );
  }
}

function ExpirationType({ methods }: { methods: UseFormReturn<CouponFormData, any, undefined> }) {
  const { t, msg } = useTranslation();

  return (
    <LabelAndSelect
      methods={methods}
      fieldName="expirationType"
      label={t(msg.coupon.expirationType)}
      topLabelCss="block"
      options={[
        { label: t(msg.general.day), value: "day" },
        { label: t(msg.general.date), value: "date" },
      ]}
    />
  );
}

function ExpirationDateOrDay({ methods }: { methods: UseFormReturn<CouponFormData, any, undefined> }) {
  const { t, msg } = useTranslation();
  const expirationType = useWatch({ control: methods.control, name: "expirationType" });

  switch (expirationType) {
    case "date":
      return (
        <LabelAndInput
          register={methods.register}
          fieldName="expiration.date"
          type="date"
          label={t(msg.coupon.expirationDay)}
          topLabelCss="block"
          required={true}
        />
      );
    case "day":
      return (
        <LabelAndSelect
          methods={methods}
          fieldName="expiration.day"
          label={t(msg.coupon.expirationDay)}
          registerOptions={{ required: true }}
          topLabelCss="block"
          options={[
            { label: t(msg.general.monday), value: "1" },
            { label: t(msg.general.tuesday), value: "2" },
            { label: t(msg.general.wednesday), value: "3" },
            { label: t(msg.general.thursday), value: "4" },
            { label: t(msg.general.friday), value: "5" },
            { label: t(msg.general.saturday), value: "6" },
            { label: t(msg.general.sunday), value: "7" },
          ]}
        />
      );
    default:
      return null;
  }
}

function SubmitButton({ editingMode }: { editingMode: EditingMode }) {
  const { t, msg } = useTranslation();

  function getSubmitBtnText() {
    switch (editingMode) {
      case EditingMode.create:
        return t(msg.coupon.addCoupon);
      case EditingMode.edit:
        return t(msg.coupon.editCoupon);
    }
  }

  return (
    <SubmitPanel>
      <Link href="/coupons" className="text-red-400">
        {t(msg.form.cancel)}
      </Link>
      <input type="submit" value={getSubmitBtnText()} />
    </SubmitPanel>
  );
}
function sanitizeCouponFormData(data: CouponFormData) {
  if ((data.eligibility.orderOverAmt as any) === "") {
    delete (data.eligibility as any).orderOverAmt;
  }

  if ((data.eligibility.userUsageLimit as any) === "") {
    delete (data.eligibility as any).userUsageLimit;
  }

  if ((data.eligibility.userUsageLimitDuration as any) === "") {
    delete (data.eligibility as any).userUsageLimitDuration;
  }

  if ((data.eligibility.shopUsageLimit as any) === "") {
    delete (data.eligibility as any).shopUsageLimit;
  }

  if ((data.eligibility.shopUsageLimitDuration as any) === "") {
    delete (data.eligibility as any).shopUsageLimitDuration;
  }

  if ((data.eligibility.firstTimeRegAfter as any) === "" || !data.eligibility.firstTimeRegAfter) {
    delete (data.eligibility as any).firstTimeRegAfter;
  }

  if (!data.eligibility.includeMembershipIds || data.eligibility.includeMembershipIds.length === 0) {
    delete (data.eligibility as any).includeMembershipIds;
  }

  if (!data.eligibility.excludeMembershipIds || data.eligibility.excludeMembershipIds.length === 0) {
    delete (data.eligibility as any).excludeMembershipIds;
  }

  if (!data.eligibility.productRestriction?.type || data.eligibility.productRestriction.type === "none") {
    delete (data.eligibility as any).productRestriction;
  } else if (data.eligibility.productRestriction.restrictToIds?.length === 0) {
    delete (data.eligibility.productRestriction as any).restrictToIds;
  }

  if (!data.eligibility.categoryRestriction?.type || data.eligibility.categoryRestriction.type === "none") {
    delete (data.eligibility as any).categoryRestriction;
  } else if (data.eligibility.categoryRestriction.restrictToIds?.length === 0) {
    delete (data.eligibility.categoryRestriction as any).restrictToIds;
  }

  if (!data.eligibility.placementRestriction?.type || data.eligibility.placementRestriction.type === "none") {
    delete (data.eligibility as any).placementRestriction;
  } else {
    if (!data.eligibility.placementRestriction.restrictToRetailIds || data.eligibility.placementRestriction.restrictToRetailIds.length === 0) {
      delete (data.eligibility.placementRestriction as any).restrictToRetailIds;
    }
    if (data.eligibility.placementRestriction.restrictToEshop === false) {
      delete (data.eligibility.placementRestriction as any).restrictToEshop;
    }
  }

  if ((data.discount.per as any) === "") {
    delete (data.discount as any).per;
  }

  if ((data.discount.amt as any) === "") {
    delete (data.discount as any).amt;
  }

  if ((data.startDate as any) === "") {
    delete (data as any).startDate;
  }

  if ((data.expiration.date as any) === "") {
    delete (data.expiration as any).date;
  }

  if ((data.expiration.day as any) === "") {
    delete (data.expiration as any).day;
  }

  if (data.expirationType === "date") {
    delete (data.expiration as any).day;
  }

  if (data.expirationType === "day") {
    delete (data.expiration as any).date;
  }

  if (data.discountType === "percent") {
    delete data.discount.amt;
  }

  if (data.discountType === "fixed") {
    delete data.discount.per;
  }
}

function AutoIssueCriteria({ methods }: { methods: UseFormReturn<CouponFormData> }) {
  const { t, msg } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      {/* <Selector
        methods={methods}
        fieldName="autoIssueCriteria.placement"
        label="Placement"
        options={[
          { name: t(msg.placement.eshop), value: "eshop" },
          { name: t(msg.placement.retail), value: "retail" },
          { name: t(msg.placement.both), value: "both" },
        ]}
      /> //TODO  */}
      <LabelAndInput
        step="0.01"
        register={methods.register}
        fieldName="autoIssueCriteria.minTriggerAmt"
        label={t(msg.membership.minTriggerAmount)}
        labelCss=""
        topLabelCss="block"
        type="number"
        errors={methods.formState.errors}
        registerOptions={{ required: true }}
      />

      <FancyCheckbox value={true} register={methods.register("autoIssueCriteria.firstTimeReg")}>
        {t(msg.membership.firstTimeIssue)}
      </FancyCheckbox>
    </div>
  );
}
